import {
  IChartProps,
  IStatisticsResponseModel,
  IVisitorsResponseModel,
} from "../models/chart";
import {
  ModelType,
  StatisticsDataInput,
  TimePeriod,
  eventType,
} from "../../../../../API";
import { useEffect, useMemo, useState } from "react";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import { DEFAULT_STATISTICS_DATE_FORMAT } from "../../system-health/common/values";
import { createChartTooltip } from "../../system-health/common/chartTooltip";
import dayjs from "dayjs";
import { useGetStatisticsData } from "./useGetStatisticsData";
import { useTheme } from "@mui/material/styles";

export const useVisitorsChart = (
  timePeriod: TimePeriod,
  locations: string[] | null
): IChartProps => {
  const theme = useTheme();

  const filterParams = useMemo(
    (): Pick<
      StatisticsDataInput,
      "eventType" | "models" | "timePeriod" | "locations"
    > => ({
      eventType: eventType.visitors,
      models: [ModelType.agg],
      locations,
      timePeriod,
    }),
    [timePeriod]
  );

  const { data, loading, refetching, refetchChartData } =
    useGetStatisticsData<IStatisticsResponseModel[]>(filterParams);

  const options: ApexOptions = useMemo(() => {
    return {
      chart: {
        stacked: false,
        width: "100%",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        fontFamily: "Inter",
      },
      theme: {
        mode: theme.palette.mode,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: data.length === 1 ? 4 : 2,
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      colors: ["#F3B344"],
      yaxis: {
        opposite: true,
      },
    };
  }, [theme.palette.mode]);

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);
  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  useEffect(() => {
    const series = generateChartSeries(data);
    const extendedOptions = generateChartCategories(data);

    setChartProps(series);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const tooltip = useMemo(() => createChartTooltip(), []);

  const generateChartSeries = (
    response: IVisitorsResponseModel[]
  ): ApexOptions["series"] => {
    if (!Array.isArray(response) || response.length === 0) {
      return [];
    }

    const seriesData = response.map(r => +r.record_count) as number[];

    const series = [
      {
        name: "Total attendances",
        data: seriesData,
        type:
          seriesData.filter(el => el !== null)?.length === 1
            ? "scatter"
            : "line",
      },
    ];

    return series;
  };

  const generateChartCategories = (
    response: IVisitorsResponseModel[]
  ): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    const categories = response.map(s =>
      dayjs(new Date(s.date)).format(DEFAULT_STATISTICS_DATE_FORMAT)
    );

    const extendedOptions: ApexOptions = {
      ...options,
      xaxis: {
        categories,
        type: "datetime",
        tickAmount: 12,
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: function (value) {
            return dayjs(new Date(value)).format(
              DEFAULT_STATISTICS_DATE_FORMAT
            );
          },
        },
      },
      tooltip,
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    refetching,
    refetchChartData,
    showNoData:
      !loading && !refetching && !data?.filter(d => d.record_count).length,
  };
};
